import { FC, ReactNode, useState } from "react"

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { IGroupImportResult } from "ts/interfaces/Group"

import {
  getSkippedRowsCount,
  isNothingToImport,
  studentName,
} from "./addMissingStudentsResult"
import DialogFooter from "./DialogFooter"
import ImportIssuesList from "./ImportIssuesList"
import SyncedClassWarning from "./SyncedClassWarning"

interface IReviewSectionProps {
  title: string
  count: number
  children?: ReactNode
}

interface IReviewListProps {
  label: string
  items: { key: string | number; text: string }[]
}

interface IAddMissingStudentsReviewProps {
  result: IGroupImportResult
  isSyncedClass: boolean
  isLoading: boolean
  onBack: () => void
  onConfirm: () => void
}

const ReviewSection: FC<IReviewSectionProps> = ({ title, count, children }) => (
  <Stack gap={1}>
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Typography
        component="h3"
        variant="body1"
        fontWeight="500"
        color="mockup.neutral0"
      >
        {title}
      </Typography>
      <Typography variant="body1" fontWeight="500" color="mockup.darkBlue">
        {count}
      </Typography>
    </Stack>
    {children}
  </Stack>
)

const ReviewList: FC<IReviewListProps> = ({ label, items }) => (
  <Box
    component="ul"
    tabIndex={0}
    aria-label={label}
    sx={(theme) => ({
      m: 0,
      py: 1,
      pl: 3.5,
      maxHeight: "220px",
      overflowY: "auto",
      border: `1px solid ${theme.palette.mockup.primary95}`,
      borderRadius: "8px",
    })}
  >
    {items.map((item) => (
      <Typography
        component="li"
        variant="body2"
        color="mockup.neutral10"
        key={item.key}
      >
        {item.text}
      </Typography>
    ))}
  </Box>
)

const AddMissingStudentsReview: FC<IAddMissingStudentsReviewProps> = ({
  result,
  isSyncedClass,
  isLoading,
  onBack,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const [showSkippedRows, setShowSkippedRows] = useState(false)
  const enrolledCount = result.enrolled.length
  const skippedRowsCount = getSkippedRowsCount(result)
  const isEmpty = isNothingToImport(result)

  const confirmLabel =
    enrolledCount > 0 && result.imported > 0
      ? `${t("group.importConfirmAddStudents", { count: enrolledCount })} ${t(
          "group.importConfirmAndCreateGroups",
          { count: result.imported },
        )}`
      : enrolledCount > 0
        ? t("group.importConfirmAddStudents", { count: enrolledCount })
        : t("group.importConfirmCreateGroups", { count: result.imported })

  const existingGroupsNote = result.skipped_groups.length > 0 && (
    <Stack gap="6px">
      <Typography variant="body2" color="mockup.neutral10">
        {t("group.importPreviewExistingGroups")}
      </Typography>
      <ReviewList
        label={t("group.importPreviewExistingGroups")}
        items={result.skipped_groups.map((name) => ({ key: name, text: name }))}
      />
    </Stack>
  )

  return (
    <Stack gap={3}>
      {isEmpty && (
        <Stack gap={1}>
          <Typography variant="body1" color="mockup.neutral10" fontWeight="500">
            {t("group.importPreviewNothingToImport")}
          </Typography>
          {existingGroupsNote}
        </Stack>
      )}
      {!isEmpty && isSyncedClass && enrolledCount > 0 && <SyncedClassWarning />}
      {(!isEmpty || skippedRowsCount > 0) && (
        <Stack gap={2} divider={<Divider orientation="horizontal" />}>
          {!isEmpty && enrolledCount > 0 && (
            <ReviewSection
              title={t("group.importPreviewStudentsToAdd")}
              count={enrolledCount}
            >
              <ReviewList
                label={t("group.importPreviewStudentsToAdd")}
                items={result.enrolled.map((student) => ({
                  key: student.id,
                  text: studentName(student),
                }))}
              />
            </ReviewSection>
          )}
          {!isEmpty && (
            <ReviewSection
              title={t("group.importPreviewGroupsToCreate")}
              count={result.imported}
            >
              {existingGroupsNote}
            </ReviewSection>
          )}
          {skippedRowsCount > 0 && (
            <ReviewSection
              title={t("group.importPreviewSkippedRows")}
              count={skippedRowsCount}
            >
              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                columnGap={2}
              >
                <Typography variant="body2" color="mockup.neutral10">
                  {t("group.importPreviewSkippedRowsDescription", {
                    count: skippedRowsCount,
                  })}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  aria-expanded={showSkippedRows}
                  sx={{
                    px: 0,
                    ":focus:not(:focus-visible)": { outline: "none" },
                  }}
                  onClick={() => setShowSkippedRows((isShown) => !isShown)}
                >
                  {showSkippedRows
                    ? t("group.importPreviewHideRows")
                    : t("group.importPreviewShowRows")}
                </Button>
              </Stack>
              <Collapse in={showSkippedRows} unmountOnExit>
                {/* Students are matched against the whole school in this mode */}
                <Stack gap={2}>
                  <ImportIssuesList
                    titleKey="group.invalidRowsTitle"
                    issues={result.invalid_rows}
                    scope="SCHOOL"
                  />
                  <ImportIssuesList
                    titleKey="group.unmatchedRowsTitle"
                    issues={result.unmatched_rows}
                    scope="SCHOOL"
                  />
                </Stack>
              </Collapse>
            </ReviewSection>
          )}
        </Stack>
      )}
      <DialogFooter>
        <Button
          size="small"
          variant="outlined"
          onClick={onBack}
          disabled={isLoading}
        >
          {t("group.importPreviewUploadAnother")}
        </Button>
        {!isEmpty && (
          <Button size="small" onClick={onConfirm} disabled={isLoading}>
            <Stack
              color="common.white"
              gap={0.5}
              direction="row"
              alignItems="center"
            >
              {isLoading && <CircularProgress size="20px" color="inherit" />}
              {confirmLabel}
            </Stack>
          </Button>
        )}
      </DialogFooter>
    </Stack>
  )
}

export default AddMissingStudentsReview
